// src/pages/admin/Enrollments.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserPlus,
  ClipboardList,
  ArrowRight,
  Clock,
  FileCheck2,
  CheckCircle2,
  FileText,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { manualEnrollmentService, ManualEnrollmentRecord } from "src/services/manualEnrollmentService";
import { ROUTES } from "src/routes/RouteConfig";

interface EnrollmentStats {
  total: number;
  enrolled: number;
  awaitingInvoice: number;
  readyToFinalize: number;
}

const Enrollments: React.FC = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const [stats, setStats] = useState<EnrollmentStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTitle("Enrollments");
    setDescription("Manage manual admissions, verified offline payments, and curriculum subscriptions");

    const fetchStats = async () => {
      try {
        const records: ManualEnrollmentRecord[] = await manualEnrollmentService.list();
        setStats({
          total: records.length,
          enrolled: records.filter((r) => r.enrollment_state === "enrolled").length,
          awaitingInvoice: records.filter((r) => r.enrollment_state === "awaiting_invoice_upload").length,
          readyToFinalize: records.filter((r) => r.enrollment_state === "ready_to_finalize").length,
        });
      } catch (e) {
        // Stats are supplementary telemetry; silently fallback if not reachable
        setStats(null);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchStats();

    return () => reset();
  }, [setTitle, setDescription, reset]);

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <button
          type="button"
          onClick={() => navigate("/admin/dashboard")}
          className="hover:text-purple-700 transition"
        >
          Admin
        </button>
        <span>/</span>
        <span className="font-semibold text-slate-800">Enrollments</span>
      </div>

      {/* Operational Telemetry Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => navigate(ROUTES.ADMIN_VIEW_ENROLLMENTS.path)}
          className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs hover:border-purple-300 hover:bg-purple-50/20 text-left transition-all"
        >
          <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            Total Records
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2 font-mono">
            {isLoading ? "—" : stats?.total ?? 0}
          </div>
        </button>

        <button
          type="button"
          onClick={() => navigate(`${ROUTES.ADMIN_VIEW_ENROLLMENTS.path}?status=enrolled`)}
          className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:bg-emerald-50/20 text-left transition-all"
        >
          <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Enrolled
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2 font-mono">
            {isLoading ? "—" : stats?.enrolled ?? 0}
          </div>
        </button>

        <button
          type="button"
          onClick={() => navigate(`${ROUTES.ADMIN_VIEW_ENROLLMENTS.path}?status=awaiting_invoice_upload`)}
          className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs hover:border-amber-300 hover:bg-amber-50/20 text-left transition-all"
        >
          <div className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            Awaiting Invoice
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2 font-mono">
            {isLoading ? "—" : stats?.awaitingInvoice ?? 0}
          </div>
        </button>

        <button
          type="button"
          onClick={() => navigate(`${ROUTES.ADMIN_VIEW_ENROLLMENTS.path}?status=ready_to_finalize`)}
          className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:bg-blue-50/20 text-left transition-all"
        >
          <div className="text-xs font-semibold text-blue-700 flex items-center gap-1.5">
            <FileCheck2 className="w-3.5 h-3.5 text-blue-600" />
            Ready to Finalize
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2 font-mono">
            {isLoading ? "—" : stats?.readyToFinalize ?? 0}
          </div>
        </button>
      </div>

      {/* Main Workflow Menu Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Manual Enrollment Card */}
        <Link
          to={ROUTES.ADMIN_ENROLLMENTS_CREATE.path}
          className="group relative flex flex-col justify-between p-6 bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md hover:border-purple-300 hover:bg-purple-50/10 transition-all duration-200"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center transition-transform group-hover:scale-105">
                <UserPlus className="w-6 h-6" />
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                Admissions Intake
              </span>
            </div>

            <h3 className="text-lg font-bold text-slate-900 group-hover:text-purple-700 transition-colors">
              Manual Enrollment
            </h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Enroll a student directly with verified offline payment settlements, modular curriculum scope selection, custom pricing, and installment plans.
            </p>

            <ul className="mt-5 space-y-2 text-xs text-slate-600">
              <li className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Curriculum scope & objective-level target selection</span>
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Offline payment receipt & installment tracking</span>
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Private invoice generation and verification</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-purple-700">
            <span>Start Manual Enrollment</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* View Enrollments Card */}
        <Link
          to={ROUTES.ADMIN_VIEW_ENROLLMENTS.path}
          className="group relative flex flex-col justify-between p-6 bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md hover:border-purple-300 hover:bg-purple-50/10 transition-all duration-200"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center transition-transform group-hover:scale-105">
                <ClipboardList className="w-6 h-6" />
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                Directory & Records
              </span>
            </div>

            <h3 className="text-lg font-bold text-slate-900 group-hover:text-purple-700 transition-colors">
              View Enrollments
            </h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Browse, search, and audit all recorded student enrollments. Inspect curriculum scopes, download invoices, finalize drafts, and check batch eligibility.
            </p>

            <ul className="mt-5 space-y-2 text-xs text-slate-600">
              <li className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Search and filter by student, product, or workflow state</span>
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Download private PDF invoices & inspect draft terms</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                <span>Batch creation eligibility and active session extensions</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-purple-700">
            <span>Browse Enrollments Directory</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Enrollments;
